import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Field, Flex, Input, Link, Message, Text } from '@workwhile/ui'
import { ListDocument, listDocumentSchema } from 'api/user/uploadI9Document'
import React, { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { DocumentTypeSelector } from './DocumentTypeSelector'
import { getListTypeFromDocumentType } from './utils'

export const DocumentUpload = ({
  onAddDocument,
  documents,
}: {
  onAddDocument: (data: ListDocument) => void
  documents: ListDocument[]
}) => {
  const [error, setError] = useState<string>('')
  const { register, watch, handleSubmit, control, setValue } =
    useForm<ListDocument>({
      resolver: zodResolver(listDocumentSchema),
      mode: 'onChange',
    })
  const documentType = watch('documentType')
  const imageUrl = {
    frontImage: useState<string | null>(null),
    backImage: useState<string | null>(null),
  }

  const requiresBarcode = useMemo(() => {
    if (!documentType) return false

    return [
      'passport',
      'foreign_passport_i551',
      'foreign_passport_i94',
      'fsm_rmi_passport',
    ].includes(documentType)
  }, [documentType])

  const onImageSelect = useMemo(
    () => (fieldName: 'frontImage' | 'backImage') => (e) => {
      const image = e.target.files?.[0]
      const [url, setUrl] = imageUrl[fieldName]

      if (url) {
        URL.revokeObjectURL(url)
      }
      setUrl(URL.createObjectURL(image))
      setValue(fieldName, image)
    },
    [imageUrl, setValue]
  )

  return (
    <Flex flexDirection="column">
      <Controller
        control={control}
        {...register('documentType')}
        render={({ field, fieldState }) => (
          <DocumentTypeSelector
            documents={documents}
            error={fieldState.error?.message}
            value={field.value}
            onChange={(value) => {
              field.onChange(value)
              setValue('listType', getListTypeFromDocumentType(value))
            }}
          />
        )}
      />

      <Controller
        control={control}
        {...register('name')}
        render={({ field, fieldState }) => (
          <Field
            label="First and last name on document"
            error={fieldState.error?.message}
          >
            <Input
              {...field}
              required
              placeholder="e.g. Elizabeth Smith"
              style={{ width: '100%' }}
            />
          </Field>
        )}
      />

      <Controller
        control={control}
        {...register('documentNumber')}
        render={({ field, fieldState }) => (
          <Field label="Document Number" error={fieldState.error?.message}>
            <Input
              {...field}
              placeholder="e.g. A123456"
              style={{ width: '100%' }}
            />
          </Field>
        )}
      />

      <Controller
        {...register('issuingAuthority')}
        control={control}
        render={({ field, fieldState }) => (
          <Field label="Issuing Authority" error={fieldState.error?.message}>
            <Input
              {...field}
              required
              placeholder="e.g. Department of State of Illinois"
              style={{ width: '100%' }}
            />
          </Field>
        )}
      />

      <Controller
        control={control}
        {...register('expirationDate')}
        render={({ field, fieldState }) => (
          <Field
            label="Expiration date (if present)"
            error={fieldState.error?.message}
          >
            <Input
              type="date"
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
            />
          </Field>
        )}
      />

      <Controller
        control={control}
        {...register('dateOfBirth')}
        render={({ field, fieldState }) => (
          <Field
            label="Date of birth (if present)"
            error={fieldState.error?.message}
          >
            <Input
              type="date"
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
            />
          </Field>
        )}
      />

      <Controller
        control={control}
        {...register('frontImage')}
        render={({ field, fieldState }) => {
          const [url] = imageUrl.frontImage
          return (
            <Field
              label={requiresBarcode ? 'Barcode Page' : 'Front Image'}
              error={fieldState.error?.message}
            >
              {field.value && url ? (
                <img
                  src={url}
                  alt="Front document preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: 'lightGray',
                  }}
                />
              ) : (
                <input
                  onChange={onImageSelect('frontImage')}
                  type="file"
                  accept="image/*"
                  style={{ width: '100%', marginBottom: 8 }}
                />
              )}
            </Field>
          )
        }}
      />
      {!requiresBarcode && (
        <Controller
          control={control}
          {...register('backImage')}
          render={({ field, fieldState }) => {
            const [url] = imageUrl.backImage
            return (
              <Field label="Back Image" error={fieldState.error?.message}>
                {field.value && url ? (
                  <img
                    src={url}
                    alt="Preview of back of document"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      backgroundColor: 'lightGray',
                    }}
                  />
                ) : (
                  <input
                    onChange={onImageSelect('backImage')}
                    type="file"
                    accept="image/*"
                    style={{ width: '100%', marginBottom: 8 }}
                  />
                )}
              </Field>
            )
          }}
        />
      )}
      <Field error={error}>
        <Button
          type="button"
          onClick={handleSubmit(
            (data) => onAddDocument(data as ListDocument),
            (errors) => {
              setError(
                Object.values(errors)
                  .map((error) => error?.message)
                  .join(', ')
              )
            }
          )}
        >
          Add Document
        </Button>
      </Field>

      <Message variant="warning">
        Do not forget to click "Upload Documents" after all documents have been
        added.
      </Message>

      <Link
        type="link"
        to="https://www.uscis.gov/i-9-central/form-i-9-acceptable-documents"
        target="_blank"
        rel="noreferrer"
      >
        <Text my={3} fontWeight="bold" textAlign="center" color="inherit">
          Acceptable I-9 Document Examples
        </Text>
      </Link>
    </Flex>
  )
}
